import { Tooltip } from "@ark-ui/solid";
import { useNavigate } from "@solidjs/router";
import { animate } from "motion";
import {
	type Component,
	createMemo,
	createSignal,
	For,
	Index,
	type JSX,
	Match,
	onMount,
	Show,
	Switch
} from "solid-js";
import { Portal } from "solid-js/web";
import { twJoin } from "tailwind-merge";
import CreditsIcon from "~/Icons/16_Icon_Credits.svg?component-solid";
import InfoIcon from "~/Icons/18_Icon_Info.svg?component-solid";
import DoneDarkIcon from "~/Icons/24_Icon_Dark_Done.svg?component-solid";
import DotIcon from "~/Icons/24_Icon_Dot.svg?component-solid";
import PhotoIcon from "~/Icons/24_Icon_photo.svg?component-solid";
import TextFormatIcon from "~/Icons/24_Icon_textformat.svg?component-solid";
import VideoIcon from "~/Icons/24_Icon_video.svg?component-solid";
import DoneWhiteIcon from "~/Icons/24_Icon_White_Done.svg?component-solid";
import StarIcon from "~/Icons/starPremiumIcon.svg?component-solid";
import TipFrameIcon from "~/Icons/TipFrameIcon.svg?component-solid";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import {
	type FeatureItem,
	type IBaseCostGeneration,
	type IPlan,
	type IProduct,
	maxWorkWidthLength,
	type TariffWordWith,
	tariffToCapacity,
	tariffToGenerationAmount,
	tariffToIncludesv1,
	tariffToIncludesv2,
	tariffToIncludesv3,
	tariffToTitle,
	tariffToWorkWith,
	tariffToWorkWithV3
} from "~/solidJs/entities/PremiumPopUp/helpers/IProducts";
import { PricingPageMethods } from "~/solidJs/pages/PricingPage";
import { PaymentApi } from "~/solidJs/shared/api/PaymentApi";
import {
	getFileSrcToCdnOrRelative,
	getGenerationCountFromBaseCost,
	getPrivateMessageFromError,
	isUserFree,
	useCustomContext
} from "~/solidJs/shared/helpers";
import { UserSubscriptionPlanEnum } from "~/solidJs/shared/helpers/types/IUser";
import { constructPricingUnsubscribeLink } from "~/solidJs/shared/models/pageParams/unsubscribePagePrams";
import { Button, type ButtonMainProps } from "~/solidJs/shared/ui";
import { Badge } from "~/solidJs/shared/ui/atoms/Badge/Badge";
import { ButtonExtraBold } from "~/solidJs/shared/ui/atoms/Button/Button";
import { Divider } from "~/solidJs/shared/ui/atoms/Divider/Divider";
import { showConfirmPlanChangeModal } from "~/solidJs/widgets/ConfirmPlanChangeModal";
import { showCreateTeamModal } from "~/solidJs/widgets/CreateTeamModalWidget/methods";
import { EndOfSubscriptionCounter } from "../../EndOfSubscriptionCounter/EndOfSubscriptionCounter";
import {
	PriceDisplayerV1,
	PriceDisplayerV2,
	PriceDisplayerV3
} from "../../PriceDisplayer/PriceDisplayer";
import { TariffBtnModeEnum } from "../helpers/tariffLookUpModes";
import { PremiumPopUpContext } from "../models/premiumPopUpContext";
import { checkoutClickHandler } from "./shared/handlers";

type CheckoutHookParams = {
	nameId: UserSubscriptionPlanEnum;
	btnMode?: TariffBtnModeEnum;
	sourceGlobalId?: string;
	isTrial?: boolean;
};

type CheckoutHookParamsV2 = {
	nameId: () => UserSubscriptionPlanEnum;
	btnMode: () => TariffBtnModeEnum | undefined;
	sourceGlobalId: () => string | undefined;
	isTrial: () => boolean | undefined;
	isFree: () => boolean | undefined;
	title: () => string | undefined;
};

const useCheckoutHandler = (params: CheckoutHookParams) => {
	const [getIsPending, setIsPending] = createSignal(false);
	const { getPremiumPopUpStore, setPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);

	const showUnsubscribeQuestions = () => {
		PaymentApi.logTryUnsubscribe();
		setPremiumPopUpStore((prev) => ({
			...prev,
			isUnsubscribeQuestionsVisible: true
		}));
	};

	const handleCheckout = async () => {
		try {
			const activeInterval = getPremiumPopUpStore().activeInterval;
			EventAnalytics.premiumBannerBtnClick(
				params.btnMode || TariffBtnModeEnum.Upgrade,
				activeInterval,
				params.nameId,
				params.sourceGlobalId,
				params.isTrial,
				getPremiumPopUpStore().source
			);

			if (params.btnMode === TariffBtnModeEnum.Current) return;

			const isRuCards = getPremiumPopUpStore().isRuCards;
			if (params.btnMode === TariffBtnModeEnum.Downgrade) {
				showUnsubscribeQuestions();
				return;
			}

			setIsPending(true);
			try {
				await checkoutClickHandler({
					...params,
					type: isRuCards ? "yookassa" : "stripe",
					promoCode: getPremiumPopUpStore().promocodeChange?.promoCode,
					activeInterval: activeInterval
				});
			} catch (e) {
				EventAnalytics.sendErrorInfo("product-card-checkout-handler-error", {
					message: getPrivateMessageFromError(e)
				});
				console.error(e);
			}
		} catch (e) {
			console.error(e);
			EventAnalytics.sendErrorInfo("premium-btn-click-error", {
				message: getPrivateMessageFromError(e)
			});
		} finally {
			setIsPending(false);
		}
	};

	return {
		handleCheckout,
		isPending: getIsPending,
		showUnsubscribeQuestions
	};
};
const useCheckoutHandlerV2 = (params: CheckoutHookParamsV2) => {
	const [getIsPending, setIsPending] = createSignal(false);
	const store = useCustomContext(PricingPageMethods.PricingPageContext);
	const navigate = useNavigate();
	const getParams = () => ({
		nameId: params.nameId(),
		btnMode: params.btnMode(),
		sourceGlobalId: params.sourceGlobalId(),
		isTrial: params.isTrial(),
		isFree: params.isFree(),
		title: params.title()
	});

	const showUnsubscribeQuestions = () => {
		PaymentApi.logTryUnsubscribe();
	};

	const handleCheckout = async () => {
		try {
			const params = getParams();
			if (params.btnMode === TariffBtnModeEnum.Upgrade && !isUserFree(getUserStore())) {
				showConfirmPlanChangeModal(params.nameId, "upgrade");
				return;
			}

			if (
				!isUserFree(getUserStore()) &&
				!params.isFree &&
				params.btnMode === TariffBtnModeEnum.Downgrade
			) {
				showConfirmPlanChangeModal(params.nameId, "downgrade");
				return;
			}

			const activeInterval = store.getSubscriptionLength();
			EventAnalytics.checkoutInitiated(params.nameId);

			if (params.btnMode === TariffBtnModeEnum.Current) return;

			const isRuCards = store.getIsRuCards();
			if (params.btnMode === TariffBtnModeEnum.Downgrade) {
				const url = constructPricingUnsubscribeLink({
					downgradeToType: params.nameId,
					downgradeToName: params.title
				});
				PaymentApi.logTryUnsubscribe();
				navigate(`${url.pathname}${url.search}`);
				return;
			}

			setIsPending(true);
			try {
				await checkoutClickHandler({
					...params,
					type: isRuCards ? "yookassa" : "stripe",
					promoCode: store.getPromo(),
					activeInterval: activeInterval
				});
			} catch (e) {
				EventAnalytics.sendErrorInfo("product-card-checkout-handler-error", {
					message: getPrivateMessageFromError(e)
				});
				console.error(e);
			}
		} catch (e) {
			console.error(e);
			EventAnalytics.sendErrorInfo("premium-btn-click-error", {
				message: getPrivateMessageFromError(e)
			});
		} finally {
			setIsPending(false);
		}
	};

	return {
		handleCheckout,
		isPending: getIsPending,
		showUnsubscribeQuestions
	};
};

type ProductCardPropsV2 = {
	activeIntervalLabel?: string;
	selectedPlan?: string;
	sourceGlobalId?: string;
	isTrial?: boolean;
	maxFeatureLength: number;
	btnMode?: TariffBtnModeEnum;
	RenderBtn?: JSX.Element;
} & IProduct;

type ProductCardPropsV1 = {
	activeIntervalLabel?: string;
	selectedPlan?: string;
	sourceGlobalId?: string;
	isTrial?: boolean;
	btnMode?: TariffBtnModeEnum;
	RenderBtn?: JSX.Element;
	setPriceShift?: (num: number) => void;
	scrollContainerRef: HTMLElement;
	registerOnExpand?: (cb: () => void) => void;
	registerOnCollapse?: (cb: () => void) => void;
} & IProduct;

export const ProductCardV2: Component<ProductCardPropsV2> = (props) => {
	const getIsSelected = () => {
		return props.nameId === props.selectedPlan;
	};

	return (
		<div
			class={twJoin(
				"group flex w-full flex-col gap-6 rounded-2xl bg-background-gray-medium px-4 py-3",
				getIsSelected() && "bg-interaction-violet"
			)}
			data-test={"premium-product-card"}
			data-test-tariff={`premium-product-card-${props.nameId}`}
		>
			<div class="flex flex-col gap-4">
				<div class="flex flex-col gap-6">
					<div class="flex w-full justify-between">
						<div class="flex flex-col">
							<div
								class={twJoin(
									"h-max w-max font-medium text-lg text-text-primary",
									getIsSelected() && "text-white"
								)}
							>
								{props.name}
							</div>
							<div
								class={twJoin(
									"-tracking-[0.065px] font-medium text-xs leading-4",
									getIsSelected()
										? "text-[rgb(255,255,255)]/80"
										: "text-text-secondary"
								)}
							>
								{tariffToTitle[props.nameId]}
							</div>
						</div>
						<Show when={getIsSelected()}>
							<div class="h-max rounded-full bg-background-orange px-2 py-1.5 font-medium text-2xs text-white leading-[10px]">
								Most Popular
							</div>
						</Show>
					</div>
					<PriceDisplayerV2
						basePrice={props.front_price_base}
						basePriceRu={props.front_price_ru_base}
						price={props.front_price}
						priceRu={props.front_price_ru}
						isFree={props.isFree}
						isSelected={getIsSelected()}
						isNeedToHideCurrency={props.isNeedToHideCurrency}
						isHiddenMonth={props.nameId === UserSubscriptionPlanEnum.Legacy_Freemium}
					/>
				</div>
				<Show
					when={props.btnMode === TariffBtnModeEnum.Current}
					fallback={<div class="h-10" />}
				>
					<CheckoutButtonFeatureV2 {...props} isSelected={getIsSelected()} />
				</Show>
			</div>

			<WorkWithBlock isSelected={getIsSelected()} nameId={props.nameId} />
			<div class="flex flex-col gap-2">
				<div
					class={twJoin(
						"w-full font-semibold text-sm leading-[18px]",
						getIsSelected() ? "text-white" : "text-text-primary"
					)}
				>
					<Switch>
						<Match when={tariffToIncludesv2[props.nameId]}>
							{(includes) => (
								<>
									Everything in <div class="inline font-bold">{includes()}</div>,
									and:
								</>
							)}
						</Match>
						<Match when={!tariffToIncludesv2[props.nameId]}>Key features:</Match>
					</Switch>
				</div>
				<div
					class={twJoin(
						"flex flex-col font-medium text-xs",
						getIsSelected() ? "text-white" : "text-text-secondary-v2"
					)}
				>
					<For each={props.featuresV2 || Object.values(props.features)}>
						{(item) => (
							<li class="relative flex items-center justify-start gap-1.5 py-1">
								<ProductFeatureWithTooltip
									isSelected={getIsSelected()}
									description={
										typeof item === "object" ? item.description : undefined
									}
									name={typeof item === "object" ? item.name : item}
								/>
							</li>
						)}
					</For>
					<For
						each={new Array(props.maxFeatureLength - props.featuresV2.length).fill("")}
					>
						{(index) => (
							<li class="flex items-center justify-start gap-1.5 py-1 opacity-0">
								<DotIcon class="size-6" />
								{index}
							</li>
						)}
					</For>
				</div>
			</div>

			<Show when={getUserStore()?.group === props.nameId && !props.isFree}>
				<EndOfSubscriptionCounter>
					{(p) => {
						return (
							<div class={twJoin(getIsSelected() ? "text-white" : "text-purple-600")}>
								{p.children}
							</div>
						);
					}}
				</EndOfSubscriptionCounter>
			</Show>
			{/*<Show when={getUserStore()?.group === props.nameId && !props.isFree}>
				<Button
					onClick={showUnsubscribeQuestions}
					className={twJoin("z-0 underline", getIsSelected() && "text-white")}
				>
					Downgrade
				</Button>
			</Show>*/}
		</div>
	);
};

const ProductFeatureWithTooltip = (props: {
	description?: string;
	name: string;
	isSelected: boolean;
}) => {
	return (
		<Tooltip.Root closeDelay={0} openDelay={0} positioning={{ placement: "top" }}>
			<DotIcon class="size-6" />
			{props.name}
			<Tooltip.Trigger
				asChild={(props) => <InfoIcon {...props()} />}
				class={twJoin(
					"group/tooltip -translate-y-1/2 absolute top-1/2 right-0 opacity-0 group-hover:opacity-100",
					props.isSelected ? "text-white/80" : "text-text-tertiary"
				)}
			/>

			<Portal>
				<Tooltip.Positioner>
					<Tooltip.Content class="z-[200] max-w-56 rounded-md bg-[#2D2D2E] p-2 px-3 py-2 font-medium text-sm text-white leading-5">
						{props.description ||
							`IP Adapter XL copies the style and colors of an
												image, helping generate visually consistent pictures`}
						<TipFrameIcon class="-translate-x-1/2 absolute bottom-0 left-1/2 translate-y-full" />
					</Tooltip.Content>
				</Tooltip.Positioner>
			</Portal>
		</Tooltip.Root>
	);
};

const WorkWithBlock: Component<{ nameId: UserSubscriptionPlanEnum; isSelected: boolean }> = (
	props
) => {
	const items = tariffToWorkWith[props.nameId] || [];
	return (
		<div class="flex flex-col gap-1.5">
			<div
				class={twJoin(
					"font-semibold text-sm",
					props.isSelected ? "text-white" : "text-text-primary"
				)}
			>
				Work with:
			</div>
			<WorkWithListItem
				IconSelected={DoneWhiteIcon}
				IconNotSelected={DoneDarkIcon}
				isSelected={props.isSelected}
				value={tariffToGenerationAmount[props.nameId] as string}
			/>
			<div
				class={twJoin(
					"h-px w-full",
					props.isSelected ? "bg-[rgb(255,255,255)]/20" : "bg-[#DDE1E6]"
				)}
			/>
			<ul class="flex flex-col text-xs">
				<For each={items}>
					{(cap) => <WorkWithListItem isSelected={props.isSelected} {...cap} />}
				</For>
				<For
					each={new Array(maxWorkWidthLength - items.length).fill({
						value: "",
						IconSelected: () => <div class="h-6" />,
						IconNotSelected: () => <div class="h-6" />
					})}
				>
					{(cap) => (
						<div class="opacity-0">
							<WorkWithListItem isSelected={props.isSelected} {...cap} />
						</div>
					)}
				</For>
			</ul>
		</div>
	);
};

// Credits display component with dynamic calculations
export const CreditsDisplayer: Component<{
	countCredits: number;
	baseCostGeneration?: IBaseCostGeneration[];
	showDetailed?: boolean;
	isMonthly: boolean;
}> = (props) => {
	const getCalculatedValues = () => {
		if (props.baseCostGeneration) {
			const imageCount = getGenerationCountFromBaseCost(
				props.countCredits,
				props.baseCostGeneration,
				"image"
			);
			const videoCount = getGenerationCountFromBaseCost(
				props.countCredits,
				props.baseCostGeneration,
				"video"
			);
			const textCount = getGenerationCountFromBaseCost(
				props.countCredits,
				props.baseCostGeneration,
				"text"
			);
			return { imageCount, videoCount, textCount };
		}
		// Fallback to hardcoded values if no base cost data
		return { imageCount: 50, videoCount: 5, textCount: 1000 };
	};

	const values = getCalculatedValues();

	return (
		<div class="flex items-start gap-1.5">
			<div class="h-5 w-5 flex-shrink-0 text-text-primary">
				<CreditsIcon class="size-5" />
			</div>
			<div class="flex flex-col gap-1">
				<span class="text-15-semibold text-text-primary">
					{props.countCredits} {props.isMonthly ? "monthly" : "weekly"} credits
				</span>
				<span class="label-10-medium h-3.5 text-text-secondary">
					= ~{values.imageCount} images / ~{values.videoCount} videos / ~
					{values.textCount} text
				</span>
			</div>
		</div>
	);
};

// Type is now imported from IProducts.tsx

export const WorkWithBlockV3: Component<{
	nameId: UserSubscriptionPlanEnum;
	isSelected?: boolean;
	title?: string;
	countCredits?: number;
	isMonthly: boolean;
	baseCostGeneration?: IBaseCostGeneration[];
}> = (props) => {
	// Use dynamic calculations if credits and base cost are provided, otherwise fallback to hardcoded values
	const items = () => {
		if (props.countCredits && props.baseCostGeneration) {
			const imageCount = getGenerationCountFromBaseCost(
				props.countCredits,
				props.baseCostGeneration,
				"image"
			);
			const videoCount = getGenerationCountFromBaseCost(
				props.countCredits,
				props.baseCostGeneration,
				"video"
			);
			const textCount = getGenerationCountFromBaseCost(
				props.countCredits,
				props.baseCostGeneration,
				"text"
			);

			return {
				generations: [
					{
						value: `${props.countCredits} credits`,
						Icon: CreditsIcon
					}
				],
				features: [
					{
						value: `~${imageCount} images`,
						Icon: PhotoIcon
					},
					{
						value: `~${videoCount} videos`,
						Icon: VideoIcon,
						showOr: true
					},
					{
						value: `~${textCount} text generations`,
						Icon: TextFormatIcon,
						showOr: true
					}
				]
			};
		}
		// Fallback to hardcoded values
		return tariffToWorkWithV3[props.nameId];
	};

	return (
		<div class="flex w-max flex-col justify-start gap-4 pl-6">
			<div class={"caption-13-medium h-[21px] shrink-0 text-text-secondary-v2 opacity-60"}>
				{props.title || `${props.isMonthly ? "Monthly" : "Weekly"} generation:`}
			</div>
			<Show when={items()?.generations?.length}>
				<ul class="flex flex-col gap-3">
					<For each={items()?.generations}>
						{(cap) => <WorkWithListItemV3 {...cap} />}
					</For>
				</ul>
				<Show when={items()?.features?.length}>
					<Divider />
				</Show>
			</Show>
			<Show when={items()?.features?.length}>
				<ul class="flex flex-col gap-3 text-xs">
					<For each={items()?.features}>{(cap) => <WorkWithListItemV3 {...cap} />}</For>
					<For
						each={new Array(
							Math.max(0, maxWorkWidthLength - (items()?.features?.length || 0))
						).fill({
							value: "",
							Icon: () => <div class="h-6" />
						})}
					>
						{(cap) => (
							<div class="opacity-0">
								<WorkWithListItemV3 isSelected={props.isSelected} {...cap} />
							</div>
						)}
					</For>
				</ul>
			</Show>
		</div>
	);
};

export const WorkWithListItemV3: Component<
	FeatureItem & { isSelected?: boolean; showOr?: boolean }
> = (props) => {
	return (
		<li class="flex items-center justify-start gap-2 text-text-primary">
			<props.Icon class="size-6" />
			<div class={"typography-text-14-medium"}>
				<Show when={props.showOr}>
					<span class="inline-block text-text-secondary">or </span>{" "}
				</Show>
				{props.value}
			</div>
		</li>
	);
};

const WorkWithListItem: Component<TariffWordWith & { isSelected: boolean }> = (props) => {
	return (
		<li class="flex items-center gap-1.5 py-1">
			<Switch>
				<Match when={props.isSelected}>
					<props.IconSelected />
				</Match>
				<Match when={!props.isSelected}>
					<props.IconNotSelected />
				</Match>
			</Switch>
			<div
				class={twJoin(
					"font-medium text-xs leading-4",
					props.isSelected ? "text-white" : "text-text-secondary-v2"
				)}
			>
				{props.value}
			</div>
		</li>
	);
};

export const ProductCardV1: Component<ProductCardPropsV1> = (props) => {
	let featuresContainerRef: HTMLDivElement | undefined;
	let initFeaturesHeight = 0;
	const { handleCheckout, isPending, showUnsubscribeQuestions } = useCheckoutHandler({
		nameId: props.nameId,
		btnMode: props.btnMode,
		sourceGlobalId: props.sourceGlobalId,
		isTrial: props.isTrial
	});
	const { getPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);
	const getIsSelected = createMemo(() => {
		return props.nameId === props.selectedPlan;
	});
	const collapseAnimation = () => {
		if (featuresContainerRef) {
			animate(featuresContainerRef, {
				height: [null, 0],
				opacity: [1, 0],
				marginBottom: ["0px", "-18px"]
			});
		}
	};
	const expandAnimation = () => {
		if (featuresContainerRef) {
			animate(featuresContainerRef, {
				height: [0, `${initFeaturesHeight}px`],
				opacity: [0, 1],
				marginBottom: [null, "0px"]
			});
		}
	};
	onMount(() => {
		if (featuresContainerRef) {
			initFeaturesHeight = featuresContainerRef.offsetHeight;
		}
		props.registerOnExpand?.(expandAnimation);
		props.registerOnCollapse?.(collapseAnimation);
	});
	return (
		<div
			class={twJoin(
				"flex w-full flex-col gap-4 rounded-2xl bg-cover bg-pos-[50%] bg-white bg-no-repeat object-contain p-4"
			)}
			style={{
				...(getIsSelected()
					? {
							"background-image": `url(${getFileSrcToCdnOrRelative("Plus.png")})`,
							"background-position": "50%"
						}
					: {})
			}}
			data-test={"premium-product-card"}
			data-test-tariff={`premium-product-card-${props.nameId}`}
		>
			<div class="flex flex-col gap-2">
				<div
					class={twJoin(
						"h-max w-max rounded-full text-xl",
						getIsSelected() && "text-white"
					)}
				>
					{props.name}
				</div>
				<PriceDisplayerV1
					basePrice={props.front_price_base}
					basePriceRu={props.front_price_ru_base}
					price={props.front_price}
					priceRu={props.front_price_ru}
					isFree={props.isFree}
					isSelected={getIsSelected()}
					isNeedToHideCurrency={props.isNeedToHideCurrency}
				/>
			</div>
			<Switch
				fallback={
					<CheckoutButtonFeatureV1
						{...props}
						clickHandler={handleCheckout}
						isPending={isPending()}
						isSelected={getIsSelected()}
					/>
				}
			>
				<Match when={props.RenderBtn}>{props.RenderBtn}</Match>
			</Switch>

			<Show when={!getPremiumPopUpStore().collapsed}>
				<div
					ref={featuresContainerRef!}
					class="flex flex-col gap-4 overflow-hidden text-text-primary"
				>
					<div class="flex flex-col gap-2">
						<div class={twJoin("font-medium", getIsSelected() && "text-white")}>
							Capacity
						</div>
						<ul class="flex flex-col gap-2 text-xs">
							<For each={tariffToCapacity[props.nameId]}>
								{(cap) => (
									<li class="flex gap-2">
										<div
											class={twJoin(
												getIsSelected()
													? "text-[rgb(255,255,255)] text-opacity-60"
													: "text-text-secondary"
											)}
										>
											{cap.name}:
										</div>
										<div
											class={twJoin(
												getIsSelected() ? "text-white" : "text-text-primary"
											)}
										>
											{cap.value}
										</div>
									</li>
								)}
							</For>
						</ul>
					</div>
					<div class="flex flex-col gap-1">
						<div class={twJoin("font-medium", getIsSelected() && "text-white")}>
							Includes
						</div>
						<Show when={tariffToIncludesv1[props.nameId]}>
							<div
								class={twJoin(
									"flex flex-wrap items-center justify-start gap-1 text-xs",
									getIsSelected() ? "text-white" : "text-text-primary"
								)}
							>
								<For each={tariffToIncludesv1[props.nameId]}>
									{(includes) => (
										<button
											type="button"
											class={twJoin(
												"pointer-events-none p-1 text-xs",
												getIsSelected()
													? "border border-background-gray-medium bg-background-gray-medium text-black"
													: "border border-[#C9C9C9]"
											)}
										>
											{includes}
										</button>
									)}
								</For>
								and
							</div>
						</Show>

						<div class="flex flex-col gap-2 rounded-lg bg-gray-100 px-4 py-3 text-xs">
							<Show when={props.iconsFeatures} keyed>
								{(iconedFeatures) => (
									<For each={iconedFeatures}>
										{(feature) => (
											<div class="flex items-center gap-2">
												{<feature.Icon class="size-6 shrink-0" />}
												{feature.text}
											</div>
										)}
									</For>
								)}
							</Show>
							<Index each={Object.values(props.features)}>
								{(item) => (
									<li class="flex items-center gap-2 py-1">
										<div class="flex size-6 items-center justify-center">
											<StarIcon class="size-4 shrink-0 [&>path]:fill-black" />
										</div>
										{item()}
									</li>
								)}
							</Index>
						</div>
					</div>
				</div>
			</Show>

			<Show when={getUserStore()?.group === props.nameId && !props.isFree}>
				<EndOfSubscriptionCounter>
					{(p) => {
						return (
							<div class={twJoin(getIsSelected() ? "text-white" : "text-purple-600")}>
								{p.children}
							</div>
						);
					}}
				</EndOfSubscriptionCounter>
			</Show>
			<Show when={getUserStore()?.group === props.nameId && !props.isFree}>
				<Button
					onClick={showUnsubscribeQuestions}
					class={twJoin("z-0 underline", getIsSelected() && "text-white")}
				>
					Downgrade
				</Button>
			</Show>
		</div>
	);
};

type CheckoutButtonFeatureProps = {
	nameId: UserSubscriptionPlanEnum;
	name: string;
	clickHandler?: JSX.EventHandler<HTMLButtonElement, MouseEvent>;
	isSelected?: boolean;
	btnMode?: TariffBtnModeEnum;
	isPending?: boolean;
	getBtnText?: (isCurrentPlan: boolean, btnMode: TariffBtnModeEnum) => JSX.Element;
	isTrial?: boolean;
	btnProps?: ButtonMainProps<"button">;
};

export const CheckoutButtonFeatureV2: Component<CheckoutButtonFeatureProps> = (props) => {
	const { handleCheckout, isPending } = useCheckoutHandlerV2({
		nameId: () => props.nameId,
		btnMode: () => props.btnMode,
		title: () => props.name,
		sourceGlobalId: () => undefined, // Not available in CheckoutButtonFeatureProps
		isTrial: () => props.isTrial,
		isFree: () => props.nameId === UserSubscriptionPlanEnum.Free
	});
	const isCurrentPlan = () => getUserStore()?.group === props.nameId;
	return (
		<Button
			onClick={(e) => {
				handleCheckout();
				props.clickHandler?.(e);
			}}
			size={"large"}
			class={twJoin("w-full leading-4")}
			variant={props.isSelected ? "SecondaryNeutral" : "TertiaryAccent"}
			disabled={isCurrentPlan() || props.btnMode === TariffBtnModeEnum.Current}
			pending={isPending()}
			data-test={"premium-submit"}
			data-test-tariff={`premium-submit-${props.nameId}`}
			{...(props.btnProps ? props.btnProps : {})}
		>
			<Switch>
				<Match when={props.getBtnText} keyed>
					{(fc) =>
						fc(
							isCurrentPlan() || props.btnMode === TariffBtnModeEnum.Current,
							props.btnMode || TariffBtnModeEnum.Upgrade
						)
					}
				</Match>
				<Match when={isCurrentPlan() || props.btnMode === TariffBtnModeEnum.Current}>
					Current plan
				</Match>
				<Match when={props.isTrial}>Get Free Trial</Match>
				<Match when={props.btnMode === "downgrade"}>Downgrade</Match>
				<Match when={props.btnMode === "upgrade"}>Upgrade to {props.name}</Match>
				<Match when={props.btnMode === undefined}>Upgrade to {props.name}</Match>
			</Switch>
		</Button>
	);
};
export const CheckoutButtonFeatureV1: Component<CheckoutButtonFeatureProps> = (props) => {
	return (
		<Button
			onClick={(e) => {
				props.clickHandler?.(e);
			}}
			size={"medium"}
			class={twJoin(
				"w-full rounded-xl bg-gray-100 hover:bg-gray-200",
				props.isSelected && "bg-white text-interaction-violet"
			)}
			disabled={
				getUserStore()?.group === props.nameId ||
				props.btnMode === TariffBtnModeEnum.Current
			}
			pending={props.isPending}
			data-test={"premium-submit"}
			data-test-tariff={`premium-submit-${props.nameId}`}
		>
			<Switch>
				<Match
					when={
						getUserStore()?.group === props.nameId ||
						props.btnMode === TariffBtnModeEnum.Current
					}
				>
					Current plan
				</Match>
				<Match when={props.isTrial}>Get Free Trial</Match>
				<Match when={props.btnMode === "downgrade"}>Downgrade</Match>
				<Match when={props.btnMode === "upgrade"}>Upgrade to {props.name}</Match>
				<Match when={props.btnMode === undefined}>Upgrade to {props.name}</Match>
			</Switch>
		</Button>
	);
};

// V3 Components
type ProductCardPropsV3 = {
	activeIntervalLabel?: string;
	selectedPlan?: string;
	sourceGlobalId?: string;
	isTrial?: boolean;
	btnMode?: TariffBtnModeEnum;
	front_price: string;
	front_price_ru: string;
	front_price_base: string;
	front_price_base_ru: string;
	isFree: boolean;
	RenderBtn?: JSX.Element;
	isHovered?: boolean;
	baseCostGeneration?: IBaseCostGeneration[];
} & IPlan;

const ProductFeatureV3: Component<{ feature: string }> = (props) => (
	<div class="flex items-start gap-2">
		<div class="mt-0.5 h-4 w-4 flex-shrink-0">
			<svg
				width="16"
				height="16"
				viewBox="0 0 16 16"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M13.3333 4L6 11.3333L2.66667 8"
					stroke="currentColor"
					stroke-width="1.33"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
		</div>
		<span class="typography-text-14-medium flex-1 text-text-primary">{props.feature}</span>
	</div>
);

export const ProductCardV3: Component<ProductCardPropsV3> = (props) => {
	const getIsSelected = () => props.selectedPlan === props.nameId;
	const state = useCustomContext(PricingPageMethods.PricingPageContext);
	const { handleCheckout } = useCheckoutHandlerV2({
		nameId: () => props.name,
		btnMode: () => props.btnMode,
		title: () => props.title,
		isFree: () => props.isFree,
		sourceGlobalId: () => props.sourceGlobalId,
		isTrial: () => props.isTrial
	});

	const getBtnVariant = () => {
		if (props.btnMode === TariffBtnModeEnum.Downgrade) {
			return "Tertiary";
		}
		if (getIsSelected()) {
			return "Primary";
		}
		return "Secondary";
	};

	const canApplyPromoCode = () => {
		return props.nameId !== UserSubscriptionPlanEnum.Teams;
	};

	// Check if user is frozen (team member)
	const user = createMemo(() => getUserStore());
	const isPersonalPlanFrozen = createMemo(() => {
		const currentUser = user();
		if (!currentUser) return false;
		// Frozen if user is team member (subscription_status === "frozen")
		return currentUser.subscription_status === "frozen";
	});

	return (
		<div
			class={twJoin(
				"relative min-h-full rounded-3xl border-[1.5px] p-6 pb-12 transition-all duration-200",
				"flex flex-col gap-10",
				getIsSelected()
					? "border-background-violet bg-white hover:bg-interaction-white-hv"
					: "border-border-secondary bg-background-gray-light hover:bg-interaction-grey-light-hv"
			)}
		>
			<Show when={canApplyPromoCode() && state.usePromocode.getPromoCode()} keyed>
				{(promoInfo) => (
					<Badge
						class="absolute top-0 right-0"
						variant="outline"
					>{`-${promoInfo.amount}% with promocode`}</Badge>
				)}
			</Show>
			{/* Plan Info Section */}
			<div class="flex flex-col gap-4 xl:gap-14">
				{/* Plan Name */}
				<div class="flex flex-col gap-0.5">
					<h3 class="md:header-20-semibold font-semibold text-2xl text-text-primary">
						{props.title}
					</h3>
					<p class="h-[19px] text-15-medium text-text-secondary">{props.description}</p>
				</div>

				{/* Pricing Info */}
				<div class="flex flex-col justify-start gap-8">
					<div class="flex flex-col gap-8">
						{/* Price Container */}
						<div class="flex flex-col gap-4 md:gap-8">
							<PriceDisplayerV3
								basePrice={props.front_price_base || "0"}
								basePriceRu={props.front_price_base_ru || "0"}
								price={props.front_price}
								priceRu={props.front_price_ru}
								isFree={props.isFree}
								isSelected={getIsSelected()}
								isHiddenMonth={
									props.nameId === UserSubscriptionPlanEnum.Legacy_Freemium
								}
								canApplyPromoCode={canApplyPromoCode()}
							/>

							{/* Credits Container */}
							<CreditsDisplayer
								countCredits={props.count_credits}
								baseCostGeneration={props.baseCostGeneration}
								isMonthly={!props.isFree}
							/>
						</div>
					</div>

					{/* Button */}
					<ButtonExtraBold
						disabled={
							props.btnMode === TariffBtnModeEnum.Current ||
							(props.nameId !== UserSubscriptionPlanEnum.Teams &&
								isPersonalPlanFrozen())
						}
						class="w-full"
						variant={getBtnVariant()}
						data-test="premium-submit"
						data-test-tariff={`premium-submit-${props.name}-checkout`}
						{...(props.nameId === "teams"
							? {
									onClick() {
										showCreateTeamModal();
									}
								}
							: { onClick: handleCheckout })}
					>
						<Switch>
							<Match when={props.btnMode === TariffBtnModeEnum.Current}>
								Current plan
							</Match>
							<Match when={props.btnMode === TariffBtnModeEnum.Downgrade}>
								Downgrade
							</Match>
							<Match when={props.btnMode === TariffBtnModeEnum.Upgrade}>
								Upgrade
							</Match>
						</Switch>
					</ButtonExtraBold>
				</div>
			</div>

			{/* Features List */}
			<div class="custom-tw-hidden flex-col gap-2.5 md:flex">
				<Show when={tariffToIncludesv3[props.name]} keyed>
					{(prevTariff) => (
						<p class="caption-12-medium h-[21px] text-text-secondary">
							Everything in {prevTariff}, and:
						</p>
					)}
				</Show>
				<div class="flex flex-col gap-2.5">
					<For each={props.featuresV2}>
						{(feature) => <ProductFeatureV3 feature={feature.name} />}
					</For>
				</div>
			</div>
		</div>
	);
};
